import { useEffect, useState } from "react";
import api, { apiError, blobApiError, API } from "@/lib/api";
import { toast } from "sonner";
import { money, dateShort } from "@/lib/format";
import { Button } from "@/components/ui/button";
import FedExReschedulePickupDialog from "@/components/FedExReschedulePickupDialog";
import { CheckCircle2, FileDown, Link2, Loader2, Mail, MapPin, CalendarClock, AlertTriangle } from "lucide-react";

function Fact({ label, value, testid }) {
  if (!value) return null;
  return (
    <div data-testid={testid}>
      <div className="label-caps text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-sm font-semibold">{value}</div>
    </div>
  );
}

function Party({ title, p }) {
  if (!p || (!p.company && !p.street && !p.city)) return null;
  return (
    <div className="min-w-0 rounded-md border border-border bg-card/60 p-3">
      <div className="label-caps flex items-center gap-1 text-muted-foreground"><MapPin className="h-3 w-3" /> {title}</div>
      <div className="mt-1 truncate text-sm font-semibold">{p.company || "—"}</div>
      <div className="text-xs text-muted-foreground">
        {[p.street, [p.city, p.state].filter(Boolean).join(", "), p.zip].filter(Boolean).join(" · ")}
      </div>
      {(p.contact || p.phone) && (
        <div className="mt-0.5 text-xs text-muted-foreground">{[p.contact, p.phone].filter(Boolean).join(" · ")}</div>
      )}
    </div>
  );
}

export default function BookingConfirmation({ q, onViewBol, onEmail }) {
  const [linkBusy, setLinkBusy] = useState(false);
  const [bolBusy, setBolBusy] = useState(false);
  const [labelBusy, setLabelBusy] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [trackToken, setTrackToken] = useState(null);
  const [rebookOpen, setRebookOpen] = useState(false);
  const isGtz = !!q.gtz_bol_number;
  const isFedex = !!q.fedex_pro_number;
  const isRl = !!q.bol_pro_number;
  useEffect(() => {
    if (!q?.id || !(q.gtz_bol_number || q.fedex_pro_number || q.bol_pro_number)) return;
    api.get(`/quotes/${q.id}/tracking-link`).then((r) => setTrackToken(r.data.token)).catch(() => {});
  }, [q?.id, q?.gtz_bol_number, q?.fedex_pro_number, q?.bol_pro_number]);
  if (!isGtz && !isFedex && !isRl) return null;
  const b = q.booking || {};
  const carrier = b.carrier || q.gtz_carrier_name || (isGtz ? "GlobalTranz Carrier" : isFedex ? "FedEx Freight" : "R+L Carriers");
  const bol = q.gtz_bol_number || q.fedex_pro_number || q.bol_pro_number;
  const pickupScheduled = b.pickup_scheduled ?? isGtz;
  const cost = b.cost ?? q.gtz_booked_cost ?? q.carrier_cost;
  const dockWindow = b.ready_time && b.close_time ? `${b.ready_time} – ${b.close_time}` : "";
  const eta = b.estimated_delivery || q.gtz_estimated_delivery;
  const bookedBy = b.booked_by || q.gtz_booked_by || q.bol_generated_by;
  const bookedAt = b.booked_at || q.gtz_booked_at || q.bol_generated_at;
  const refs = b.refs || {};

  const copyTrackingLink = async () => {
    setLinkBusy(true);
    try {
      const { data } = await api.get(`/quotes/${q.id}/tracking-link`);
      const url = `${globalThis.location.origin}/track/${data.token}`;
      await navigator.clipboard.writeText(url);
      toast.success("Customer tracking link copied", { description: url });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLinkBusy(false);
    }
  };

  const previewGtzBol = async () => {
    setBolBusy(true);
    try {
      const res = await api.get(`/quotes/${q.id}/gtz-carrier-bol/pdf`, { responseType: "blob" });
      const url = globalThis.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      globalThis.open(url, "_blank", "noopener");
      setTimeout(() => globalThis.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(await blobApiError(e));
    } finally {
      setBolBusy(false);
    }
  };

  const previewFedexBol = async () => {
    setBolBusy(true);
    try {
      const res = await api.get(`/quotes/${q.id}/fedex-bol/pdf`, { responseType: "blob" });
      const url = globalThis.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      globalThis.open(url, "_blank", "noopener");
      setTimeout(() => globalThis.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(await blobApiError(e));
    } finally {
      setBolBusy(false);
    }
  };

  const previewFedexLabel = async () => {
    setLabelBusy(true);
    try {
      const res = await api.get(`/quotes/${q.id}/fedex-label/pdf`, { responseType: "blob" });
      const url = globalThis.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      globalThis.open(url, "_blank", "noopener");
      setTimeout(() => globalThis.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(await blobApiError(e));
    } finally {
      setLabelBusy(false);
    }
  };

  const copyPickupDebug = async () => {
    try {
      await globalThis.navigator.clipboard.writeText(JSON.stringify(q.booking?.pickup_debug || {}, null, 2));
      toast.success("FedEx pickup diagnostics copied — paste them to your developer.");
    } catch {
      toast.error("Couldn't copy — check clipboard permissions.");
    }
  };

  const copyFedexDocDiag = async () => {
    let preview = null;
    try {
      preview = (await api.post(`/quotes/${q.id}/fedex-ship-request-preview`)).data;
    } catch { /* preview is best-effort */ }
    const diag = {
      pro: q.fedex_pro_number, account: q.fedex_account_number,
      has_bol: !!q.fedex_bol_pdf_b64, has_label: !!q.fedex_label_pdf_b64,
      ship_endpoint: q.fedex_ship_endpoint || null,
      ship_http_status: q.fedex_ship_http_status || null,
      // Actual saved pickup status — so the export matches the on-screen "Pickup scheduled" state.
      pickup_scheduled: q.booking?.pickup_scheduled ?? null,
      pickup_confirmation: q.fedex_pickup_confirmation || q.booking?.refs?.pickup_confirmation || null,
      pickup_date: q.booking?.pickup_date || null,
      pickup_endpoint: q.fedex_pickup_endpoint || null,
      pickup_http_status: q.fedex_pickup_http_status || null,
      pickup_request: q.fedex_pickup_request || q.booking?.pickup_debug?.request || null,
      pickup_response: q.fedex_pickup_response || q.booking?.pickup_debug?.response || null,
      pickup_error: q.booking?.pickup_error || null,
      outgoing_request_preview: preview,
      ship_request: q.fedex_ship_request || null,
      ship_response: q.fedex_ship_response || null,
      ship_debug: q.fedex_ship_debug || null,
      bol_fetch_debug: q.fedex_bol_fetch_debug || null,
    };
    try {
      await globalThis.navigator.clipboard.writeText(JSON.stringify(diag, null, 2));
      toast.success("FedEx document diagnostics copied — paste them to your developer.");
    } catch {
      toast.error("Couldn't copy — check clipboard permissions.");
    }
  };

  const cancelFedexPickup = async () => {
    setCancelBusy(true);
    try {
      const { data } = await api.post(`/quotes/${q.id}/fedex-cancel-pickup`);
      toast.success(data.message || "FedEx pickup cancelled.");
      globalThis.location.reload();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setCancelBusy(false);
    }
  };

  return (
    <div className="rounded-md border border-[hsl(var(--success))]/50 bg-[hsl(var(--success))]/5" data-testid="booking-confirmation">
      <div className="flex flex-wrap items-center gap-2 border-b border-[hsl(var(--success))]/30 px-4 py-3">
        <CheckCircle2 className="h-5 w-5 text-[hsl(var(--success))]" />
        <span className="font-heading text-base font-bold">Shipment Booked</span>
        <span className="rounded bg-[hsl(var(--success))]/15 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-[hsl(var(--success))]">
          via {isGtz ? "GlobalTranz" : isFedex ? "FedEx Freight" : "R+L Carriers"}
        </span>
        <span className="metric-num rounded bg-card px-2 py-0.5 text-xs font-bold" data-testid="booking-bol-number">
          {isGtz ? "BOL" : "PRO"} {bol}
        </span>
        {pickupScheduled ? (
          <span className="flex items-center gap-1 text-xs font-semibold text-[hsl(var(--success))]">
            <CalendarClock className="h-3.5 w-3.5" /> Pickup scheduled
          </span>
        ) : (
          <span className="flex items-center gap-1 text-xs font-semibold text-[hsl(var(--warning))]" data-testid="booking-bol-only">
            <AlertTriangle className="h-3.5 w-3.5" /> BOL only — no pickup scheduled
          </span>
        )}
        <div className="ml-auto flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={copyTrackingLink} disabled={linkBusy}
            data-testid="booking-tracking-link" className="h-8 gap-1.5">
            {linkBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Link2 className="h-3.5 w-3.5" />} Tracking Link
          </Button>
          <Button size="sm" variant="outline" onClick={isRl ? onViewBol : isFedex ? previewFedexBol : previewGtzBol} disabled={bolBusy}
            data-testid="booking-view-bol" className="h-8 gap-1.5">
            {bolBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />} BOL PDF
          </Button>
          {isFedex && (
            <Button size="sm" variant="outline" onClick={previewFedexLabel} disabled={labelBusy}
              data-testid="booking-view-label" className="h-8 gap-1.5">
              {labelBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />} Label
            </Button>
          )}
          {isFedex && (
            <Button size="sm" variant="ghost" onClick={copyFedexDocDiag}
              data-testid="booking-copy-doc-diag" className="h-8 gap-1.5 text-muted-foreground"
              title="Copy the FedEx document (BOL/label) diagnostics for debugging">
              Copy diagnostics
            </Button>
          )}
          {isFedex && pickupScheduled && (
            <Button size="sm" variant="outline" onClick={cancelFedexPickup} disabled={cancelBusy}
              data-testid="booking-cancel-pickup" className="h-8 gap-1.5">
              {cancelBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CalendarClock className="h-3.5 w-3.5" />} Cancel Pickup
            </Button>
          )}
          {isFedex && !pickupScheduled && (
            <Button size="sm" variant="outline" onClick={() => setRebookOpen(true)}
              data-testid="booking-rebook-pickup" className="h-8 gap-1.5">
              <CalendarClock className="h-3.5 w-3.5" /> Rebook Pickup
            </Button>
          )}
          <Button size="sm" onClick={onEmail} data-testid="booking-email-confirmation"
            className="h-8 gap-1.5 bg-[hsl(var(--success))] text-white hover:bg-[hsl(var(--success))]/90">
            <Mail className="h-3.5 w-3.5" /> Email Confirmation
          </Button>
        </div>
      </div>
      {isFedex && b.pickup_error && (
        <div className="border-b border-[hsl(var(--warning))]/30 bg-[hsl(var(--warning))]/10 px-4 py-2 text-xs font-medium text-[hsl(var(--warning))]" data-testid="booking-pickup-error">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
            <span className="min-w-0 flex-1">Pickup was not scheduled with FedEx: {b.pickup_error}</span>
            {b.pickup_debug && (
              <button
                onClick={copyPickupDebug}
                data-testid="booking-copy-pickup-debug"
                className="shrink-0 rounded border border-[hsl(var(--warning))]/40 px-2 py-0.5 font-semibold hover:bg-[hsl(var(--warning))]/20"
                title="Copy the exact FedEx pickup request + response for debugging"
              >
                Copy diagnostics
              </button>
            )}
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 gap-x-6 gap-y-3 px-4 py-3 sm:grid-cols-3 lg:grid-cols-6">
        <Fact label="Carrier" value={carrier} testid="booking-carrier" />
        <Fact label="Cost at Booking" value={cost != null ? money(cost) : ""} testid="booking-cost" />
        <Fact label="Pickup Date" value={b.pickup_date ? String(b.pickup_date).slice(0, 10) : pickupScheduled ? "—" : ""} testid="booking-pickup-date" />
        <Fact label="Dock Window" value={dockWindow} testid="booking-window" />
        <Fact label="Est. Delivery" value={eta ? String(eta).slice(0, 10) : ""} testid="booking-eta" />
        <Fact label="Booked By" value={bookedBy ? `${bookedBy} · ${dateShort(bookedAt)}` : ""} testid="booking-by" />
      </div>
      {(b.origin || b.destination) && (
        <div className="grid grid-cols-1 gap-3 px-4 pb-3 md:grid-cols-2">
          <Party title="Pickup From" p={b.origin} />
          <Party title="Deliver To" p={b.destination} />
        </div>
      )}
      {(refs.po_number || refs.pickup_number || refs.pickup_confirmation || refs.shipper_ref || refs.service_level || b.instructions) && (
        <div className="flex flex-wrap gap-x-5 gap-y-1 border-t border-[hsl(var(--success))]/20 px-4 py-2 text-xs text-muted-foreground">
          {refs.shipper_ref && <span>Ref: <span className="font-semibold text-foreground">{refs.shipper_ref}</span></span>}
          {refs.po_number && <span>PO: <span className="font-semibold text-foreground">{refs.po_number}</span></span>}
          {refs.pickup_number && <span>Pickup #: <span className="font-semibold text-foreground">{refs.pickup_number}</span></span>}
          {refs.pickup_confirmation && <span>Pickup Conf: <span className="font-semibold text-foreground">{refs.pickup_confirmation}</span></span>}
          {refs.service_level && <span>Service: <span className="font-semibold text-foreground">{refs.service_level}</span></span>}
          {refs.payment_terms && <span>Terms: <span className="font-semibold text-foreground">{refs.payment_terms}</span></span>}
          {b.instructions && <span className="basis-full">Instructions: {b.instructions}</span>}
        </div>
      )}
      {trackToken && (
        <div className="flex items-center gap-3 border-t border-[hsl(var(--success))]/20 px-4 py-3">
          <img src={`${API}/public/track/${trackToken}/qr.png`} alt="Tracking QR"
            className="h-20 w-20 rounded-md bg-white p-1" data-testid="booking-qr" />
          <div className="text-xs text-muted-foreground">
            <div className="text-sm font-semibold text-foreground">Scan to track live</div>
            <div className="mt-0.5 max-w-md">Drivers and receiving docks can scan this QR for live shipment status &mdash; it&apos;s also embedded in the confirmation email.</div>
          </div>
        </div>
      )}
      {q.booking_confirmation_sent_at && (
        <div className="border-t border-[hsl(var(--success))]/20 px-4 py-2 text-xs text-muted-foreground" data-testid="booking-confirmation-sent">
          Confirmation emailed to <span className="font-semibold text-foreground">{q.booking_confirmation_sent_to}</span> by {q.booking_confirmation_sent_by} · {dateShort(q.booking_confirmation_sent_at)}
        </div>
      )}
      <FedExReschedulePickupDialog
        open={rebookOpen}
        onOpenChange={setRebookOpen}
        quote={q}
        onRebooked={() => globalThis.location.reload()}
      />
    </div>
  );
}
